import { ResponseResult, type ResponseType } from '@amzn/gbsa-helios-typescript-client';
import { isDataCollectionType } from './responseTypeClassification';

/**
 * Duck-typed subset of {@link Question} and {@link SubQuestion} that
 * exposes the two mandatory-comment flags. Accepting the shared
 * shape lets one helper serve both parent-question and sub-question
 * comment fields — those two types carry the same pair of flags but
 * disagree on nearly everything else, so pulling the full types in
 * would force every caller to pick a branch.
 */
export interface CommentRequirementTarget {
  readonly requireCommentOnFail?: boolean | undefined;
  readonly requireCommentOnPass?: boolean | undefined;
}

/**
 * Duck-typed subset of {@link Question} and {@link SubQuestion} that
 * exposes the two mandatory-evidence flags. Same rationale as
 * {@link CommentRequirementTarget} — one helper serves both parent
 * and sub attachment slots.
 */
export interface EvidenceRequirementTarget {
  readonly requireEvidenceOnFail?: boolean | undefined;
  readonly requireEvidenceOnPass?: boolean | undefined;
}

/**
 * Returns true when the target's current result requires a comment
 * per the question / sub-question configuration.
 *
 *   - FAIL result + `requireCommentOnFail` → required
 *   - PASS result + `requireCommentOnPass` → required
 *   - NA result → never required (no requireCommentOnNA flag exists)
 *   - Unanswered (result === undefined) → never required, because
 *     an auditor hasn't committed to any result yet and enforcing
 *     the hint on an empty row would nag the surface at first paint.
 *
 * Pure — no react, no hooks, no side effects. Consumed by the
 * PerformAuditQuestionComment inline hint (renders "Comment
 * required" when this returns true AND the draft is blank) and by
 * page-level close-audit blockers via {@link getQuestionViolations}
 * (which enforces the same rule on the persisted response).
 *
 * Kept as its own small module rather than folded into the
 * `auditClosureValidation` module because the read-shape
 * `getQuestionViolations` operates on the SERVER response, not the
 * in-progress draft. Two callers with different sources of truth
 * read cleaner as two named functions than one union-typed function.
 */
export function isCommentRequired(
  target: CommentRequirementTarget,
  result: ResponseResult | undefined,
): boolean {
  if (result === undefined) return false;
  if (result === ResponseResult.FAIL) return target.requireCommentOnFail === true;
  if (result === ResponseResult.PASS) return target.requireCommentOnPass === true;
  return false;
}

/**
 * Returns true when the target's current result requires photo/file
 * evidence per the question / sub-question configuration. Exact
 * mirror of {@link isCommentRequired} over the evidence flag pair —
 * same result gating (NA and unanswered are never required).
 *
 * Consumed by the perform-audit attachment slots (drives the red
 * asterisk + required note on `MediaAttachments`) and mirrors the
 * rule the close-audit validator enforces on the persisted response.
 */
export function isEvidenceRequired(
  target: EvidenceRequirementTarget,
  result: ResponseResult | undefined,
): boolean {
  if (result === undefined) return false;
  if (result === ResponseResult.FAIL) return target.requireEvidenceOnFail === true;
  if (result === ResponseResult.PASS) return target.requireEvidenceOnPass === true;
  return false;
}

/**
 * Duck-typed subset of {@link SubQuestion} exposing the UNCONDITIONAL
 * requirement flags used by Enhanced Response Types (ERT)
 * data-collection sub-questions. Data subs are never scored, so the
 * OnPass/OnFail pairs can't apply — the SDK gives them plain
 * `requireComment` / `requireAttachment` instead.
 */
export interface UnconditionalRequirementTarget {
  readonly requireComment?: boolean | undefined;
  readonly requireAttachment?: boolean | undefined;
}

/**
 * Returns true when a DATA-COLLECTION sub-question's captured value
 * makes its unconditional comment requirement active.
 *
 * The `hasValue` gate mirrors the result-gate on
 * {@link isCommentRequired}: requirements only bind once the auditor
 * has committed an answer — for a data sub that's a non-empty
 * `responseValue`, not a Pass/Fail result. An untouched sub never
 * nags; the close-audit validator flags its missing VALUE instead.
 *
 * Pure — no react, no side effects. Consumed by the sub-question
 * row's comment hint and by the close-audit validator so draft-time
 * hints and closure blockers enforce the same rule.
 */
export function isDataCollectionCommentRequired(
  target: UnconditionalRequirementTarget,
  hasValue: boolean,
): boolean {
  return hasValue && target.requireComment === true;
}

/**
 * Evidence twin of {@link isDataCollectionCommentRequired} — true
 * when a valued data-collection sub must carry an attachment per its
 * unconditional `requireAttachment` flag.
 */
export function isDataCollectionEvidenceRequired(
  target: UnconditionalRequirementTarget,
  hasValue: boolean,
): boolean {
  return hasValue && target.requireAttachment === true;
}

/**
 * Full requirement surface of a sub-question definition: the
 * conditional OnPass/OnFail pairs (scored subs), the unconditional
 * flags (data-collection subs), and the ERT `responseType` that
 * selects between them.
 */
export interface SubRequirementTarget
  extends
    CommentRequirementTarget,
    EvidenceRequirementTarget,
    UnconditionalRequirementTarget {
  readonly responseType?: ResponseType | undefined;
}

/**
 * Resolves which comment / evidence requirements currently apply to
 * a sub-question — THE branching point between the two ERT flows:
 *
 *   - data-collection sub (`responseType` is a data type) → the
 *     unconditional flags, active once the sub holds a value;
 *   - scored sub (absent / PASS_FAIL type) → the OnPass/OnFail
 *     pairs, active per the committed result.
 *
 * Pure — no react, no side effects. Consumed by the sub-question
 * row's inline hints and the PA-20 auto-advance gate so both
 * surfaces branch identically.
 */
export function resolveSubRequirements(
  target: SubRequirementTarget,
  result: ResponseResult | undefined,
  hasValue: boolean,
): { readonly commentRequired: boolean; readonly evidenceRequired: boolean } {
  if (isDataCollectionType(target.responseType)) {
    return {
      commentRequired: isDataCollectionCommentRequired(target, hasValue),
      evidenceRequired: isDataCollectionEvidenceRequired(target, hasValue),
    };
  }
  return {
    commentRequired: isCommentRequired(target, result),
    evidenceRequired: isEvidenceRequired(target, result),
  };
}
